/**
 * useLearningSession - Composable for managing learning session lifecycle
 *
 * Handles:
 * - Session initialization and cleanup
 * - Progress tracking via ProgressStore and SessionStore
 * - Course data loading with fallback to demo mode
 * - ROUND-based learning via TripleHelixEngine
 */

import { ref, computed, onMounted, onUnmounted, shallowRef, isRef, type Ref } from 'vue'
import type { SupabaseClient } from '@supabase/supabase-js'
import type { ProgressStore, SessionStore, ClassifiedBasket } from '@ssi/core'
import {
  createTripleHelixEngine,
  type TripleHelixEngine,
  DEFAULT_CONFIG,
} from '@ssi/core'
import type { CourseDataProvider, LearningItem } from '../providers/CourseDataProvider'
import { isBundleBootstrapEnabled } from './useInstantPlayback'
import { firstSound } from '../playback/firstSoundSignal'
import { useUserRole } from './useUserRole'

// Accept either a value or a Ref — read lazily to avoid setup-time null captures
type MaybeRef<T> = T | Ref<T>
function unref<T>(val: MaybeRef<T>): T {
  return isRef(val) ? val.value : val
}

export interface CheckpointFailureDetail {
  errorClass: string
  message: string
  code?: string
  sessionId: string
  itemsPracticed: number
  durationSeconds: number
  /** how many failures this session has had, including this one */
  failureCount: number
}

export interface UseLearningSessionOptions {
  progressStore?: MaybeRef<ProgressStore | null | undefined>
  sessionStore?: MaybeRef<SessionStore | null | undefined>
  /**
   * Direct supabase client — used for the speaking-opportunities counter
   * RPC because the sessionStore injection chain has been chronically
   * unreliable (the ref can read null at runtime even when populated at
   * setup). Same pattern as usePairingsTelemetry which writes successfully.
   */
  supabase?: MaybeRef<SupabaseClient | null | undefined>
  courseDataProvider?: MaybeRef<CourseDataProvider | null | undefined>
  learnerId?: MaybeRef<string | null | undefined>
  courseId?: MaybeRef<string | null | undefined>
  demoItems?: LearningItem[]
  /**
   * True while the player is PRACTISING — replaying already-covered material
   * because the next NEW LEGO could not be fetched. Suppresses LEGO progress
   * writes only; usage telemetry is unaffected. Being offline is NOT this: a
   * downloaded look-ahead of genuinely new LEGOs plays and records normally.
   */
  /** True while NO LEGO PROGRESS may be written: the PRACTISING mode, or a
   *  recycled round on the playhead. The caller composes both — see
   *  progressWritesSuppressed in LearningPlayer. */
  isPractising?: () => boolean
  /**
   * Telemetry hook (job #813): called when a session checkpoint write fails
   * (error returned or promise rejected), so a failing write is distinguishable
   * from a session that never completed a cycle. Throttled here; observation
   * only, never alters checkpointing.
   */
  onCheckpointFailed?: (detail: CheckpointFailureDetail) => void
  /**
   * True while any audio path is sounding — cycles, pod laps, commentary,
   * intros, welcome. The caller already computes this to drive
   * markPlayStart/markPlayStop; the play timer needs it a second time, on
   * return from the background, to know whether to re-arm. See
   * handleVisibilityChange.
   */
  isAudioActive?: () => boolean
}

export interface LearningSessionState {
  sessionId: Ref<string | null>
  isLoading: Ref<boolean>
  error: Ref<string | null>
  items: Ref<LearningItem[]>
  isDemoMode: Ref<boolean>
}

export interface RoundInfo {
  isInRound: boolean
  legoId: string | null
  phase: string | null
  phaseIndex: number
}

/**
 * MINUTES HANDED TO THE BANK THAT DID NOT LAND (job #222). The watermark
 * below advances by what was SENT; the minutes a failed write did not land
 * are carried here, under the learner and course, and ride the next bank
 * for that pair: this session's next boundary, or the next session's start.
 * Before this the failed delta was rolled back into the watermark, which
 * dies with the composable — a bank that fails at the session's end (the
 * unmount) was retried once in the same breath and then lost for good,
 * proven by useLearningSessionMinutesBoundary.characterisation.test.ts.
 * The carry is taken off storage before a send and put back on failure, so
 * two sends in flight cannot both carry it. Whole minutes, a floor: the
 * number may never inflate (owner ruling, 2026-08-19), and a minute written
 * server-side but reported failed is the same exposure the in-session retry
 * always had.
 */
export const UNBANKED_MINUTES_KEY = (learnerId: string, courseId: string) =>
  `ssi-unbanked-minutes:${learnerId}:${courseId}`

function readUnbanked(key: string): number {
  try {
    const n = Number(localStorage.getItem(key))
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0
  } catch { return 0 }
}
function writeUnbanked(key: string, minutes: number): void {
  try {
    if (minutes > 0) localStorage.setItem(key, String(minutes))
    else localStorage.removeItem(key)
  } catch { /* storage full or private: the minutes are still in the watermark's past */ }
}

/**
 * Bank `delta` whole minutes plus any carry for this learner and course.
 * Never throws. A write that does not land (false or a rejection) puts the
 * whole amount on the carry. POSITION IS NOT TOUCHED: highestSeed is 0.
 */
async function bankMinutes(
  store: { updateEnrollmentActivity: (l: string, c: string, s: number, m: number) => Promise<boolean> | boolean },
  learnerId: string,
  courseId: string,
  delta: number,
): Promise<void> {
  const key = UNBANKED_MINUTES_KEY(learnerId, courseId)
  const carry = readUnbanked(key)
  if (carry) writeUnbanked(key, 0)
  const total = delta + carry
  if (total <= 0) return
  let written: boolean
  try {
    written = (await store.updateEnrollmentActivity(learnerId, courseId, 0, total)) !== false
  } catch (err) {
    written = false
    console.warn('[useLearningSession] Failed to bank practice minutes:', err)
  }
  if (!written) {
    // A FALSE IS A FAILURE, NOT A NO-OP: the store reports a write it could
    // not land by returning false rather than by rejecting (job #450).
    writeUnbanked(key, readUnbanked(key) + total)
    console.warn('[useLearningSession] Practice minutes not banked; carried to the next bank')
  }
}

export function useLearningSession(options: UseLearningSessionOptions = {}) {
  const demoItems = options.demoItems ?? []
  // An ssi_admin viewing the app as a learner is LOOKING, not learning.
  const { isViewingAs } = useUserRole()

  /**
   * PRACTISING — replaying material because the next new LEGO could not be
   * fetched (Tom's ruling, 2026-08-31). Splits this composable's two jobs:
   * USAGE keeps flowing (items practised, speaking opportunities, session
   * checkpoints, minutes) because the learner genuinely turned up and
   * practised, and teachers' dashboards depend on it; LEGO PROGRESS stops,
   * because replaying completed material is not progress.
   *
   * This is NOT a connectivity check. Deliberate offline mode, working
   * through a downloaded look-ahead of genuinely new LEGOs, is the real
   * learning flow and records everything — it just syncs later.
   */
  const isPractising = () => options.isPractising?.() === true

  // Lazy accessors — these read refs at call time, not setup time
  const getProgressStore = () => unref(options.progressStore) ?? undefined
  const getSessionStore = () => unref(options.sessionStore) ?? undefined
  const getSupabase = () => unref(options.supabase) ?? undefined
  const getCourseDataProvider = () => unref(options.courseDataProvider) ?? undefined
  const getLearnerId = () => unref(options.learnerId) || 'demo-learner'
  const getCourseId = () => unref(options.courseId) || 'demo'

  /**
   * Bump the speaking-opportunities counter directly via supabase.rpc.
   * Goes around sessionStore because that injection chain has been
   * unreliable (the ref reads null at runtime in some cases). Same
   * direct-supabase pattern as usePairingsTelemetry, which writes
   * successfully on every cycle for the same flow.
   */
  /**
   * CLASS MODE (job #778): the class account cannot pass either ledger RPC —
   * `bump_speaking_opportunities` requires `learners.user_id = auth.uid()` and
   * a class's user_id is `class-learner:<classId>`, nobody's login — so every
   * class-mode bump was refused (42501) and only logged; zero ledger rows for
   * any class, ever. The class-aware progress store carries a
   * `bumpSpeakingOpportunities` that routes the same deltas through the
   * teacher-authorised /api/school/class-progress endpoint and resolves true
   * when it handled them. Outside class mode it resolves false and the RPC
   * runs exactly as before. Best-effort like the RPC: a failure is logged,
   * never thrown into the flush.
   */
  const bumpViaClassRoute = (
    learnerId: string, courseId: string, oppsDelta: number, secondsDelta: number, phrasesDelta: number,
  ): Promise<boolean> | null => {
    const store = getProgressStore() as unknown as {
      bumpSpeakingOpportunities?: (l: string, c: string, o: number, s: number, p: number) => Promise<boolean>
    } | undefined
    if (typeof store?.bumpSpeakingOpportunities !== 'function') return null
    let attempt: Promise<boolean>
    try {
      attempt = store.bumpSpeakingOpportunities(learnerId, courseId, oppsDelta, secondsDelta, phrasesDelta)
    } catch (err) {
      console.error('[useLearningSession] class ledger bump threw:', err)
      return Promise.resolve(true)
    }
    return attempt.then((handled) => handled === true, (err: unknown) => {
      console.error('[useLearningSession] class ledger bump FAILED:', err)
      return true
    })
  }

  const bumpOpportunities = (oppsDelta: number, secondsDelta: number) => {
    if (oppsDelta <= 0 && secondsDelta <= 0) return
    const supabase = getSupabase()
    const learnerId = getLearnerId()
    const courseId = getCourseId()
    if (!supabase || !learnerId || !courseId || isGuestLearner(learnerId)) return
    const viaClass = bumpViaClassRoute(learnerId, courseId, oppsDelta, secondsDelta, 0)
    if (viaClass) return viaClass.then((handled) => { if (!handled) return bumpOpportunitiesRpc(supabase, learnerId, courseId, oppsDelta, secondsDelta) })
    return bumpOpportunitiesRpc(supabase, learnerId, courseId, oppsDelta, secondsDelta)
  }

  const bumpOpportunitiesRpc = (supabase: SupabaseClient, learnerId: string, courseId: string, oppsDelta: number, secondsDelta: number) => {
    // Return the promise so callers that need the write to land before reading
    // (e.g. opening the stats modal) can await it; fire-and-forget callers ignore it.
    return supabase.rpc('bump_speaking_opportunities', {
      p_learner_id: learnerId,
      p_course_code: courseId,
      p_opps_delta: oppsDelta,
      p_seconds_delta: secondsDelta,
    }).then(({ error }: { error: any }) => {
      if (error) {
        console.error('[useLearningSession] bump_speaking_opportunities FAILED:', {
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint,
          learnerId,
          courseId,
          oppsDelta,
          secondsDelta,
        })
      }
    })
  }

  /**
   * Bump the phrases-spoken counter — cycles where the VAD actually heard the
   * learner speak. Deliberately its OWN rpc rather than a fifth argument on
   * bump_speaking_opportunities: adding a parameter there means DROP + CREATE
   * (a new parameter is a new signature), and if anything re-asserts the 4-arg
   * signature afterwards Postgres holds both overloads and every existing
   * 4-named-arg call becomes ambiguous — an outage of the opportunities and
   * play-seconds writes. This function touches neither. It only ever fires on
   * a flush where speech was actually detected, so for a learner with
   * adaptation consent off (the default) it is zero additional writes.
   *
   * See supabase/migrations/20260819_phrases_spoken_ledger.sql.
   */
  const bumpPhrasesSpoken = (phrasesDelta: number) => {
    if (phrasesDelta <= 0) return
    const supabase = getSupabase()
    const learnerId = getLearnerId()
    const courseId = getCourseId()
    if (!supabase || !learnerId || !courseId || isGuestLearner(learnerId)) return
    const viaClass = bumpViaClassRoute(learnerId, courseId, 0, 0, phrasesDelta)
    if (viaClass) return viaClass.then((handled) => { if (!handled) return bumpPhrasesSpokenRpc(supabase, learnerId, courseId, phrasesDelta) })
    return bumpPhrasesSpokenRpc(supabase, learnerId, courseId, phrasesDelta)
  }

  const bumpPhrasesSpokenRpc = (supabase: SupabaseClient, learnerId: string, courseId: string, phrasesDelta: number) => {
    return supabase.rpc('bump_phrases_spoken', {
      p_learner_id: learnerId,
      p_course_code: courseId,
      p_phrases_delta: phrasesDelta,
    }).then(({ error }: { error: any }) => {
      if (error) {
        console.error('[useLearningSession] bump_phrases_spoken FAILED:', {
          message: error.message,
          code: error.code,
          learnerId,
          courseId,
          phrasesDelta,
        })
      }
    })
  }

  // State
  const sessionId = ref<string | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const items = ref<LearningItem[]>([])
  const itemsPracticed = ref(0)
  const sessionStartTime = ref<Date | null>(null)

  // Actual play time tracking — only counts seconds where audio is playing.
  // Wall-clock duration is useless (idle tabs, backgrounded apps, etc.)
  let playSegmentStart: number | null = null
  const accumulatedPlaySeconds = ref(0)
  // Watermark for incremental persistence into learner_speaking_opportunities.
  // We write deltas (current_total - last_persisted) so we never double-count
  // and never need to know the row's existing value before writing.
  let lastPersistedPlaySeconds = 0
  // Same watermark for the MINUTES already banked into
  // course_enrollments.total_practice_minutes by this session. That column
  // accumulates, so every write has to send a delta — see
  // persistSessionSnapshot.
  let lastPersistedMinutes = 0
  let disposed = false
  let checkpointPending = false
  let endPending = false

  // Per-cycle opportunities accumulator. We deliberately do NOT bump the
  // server on every cycle (that was a write per ~11s, all noise). Cycles
  // accumulate locally and flush on the same boundaries as the play-second
  // delta: markPlayStop (pause / stop), visibility-hidden, beforeunload,
  // session end.
  let pendingOppsDelta = 0

  // Same delta/watermark treatment for phrases spoken (cycles where the VAD
  // heard the learner). This is the whole point of moving the counter here:
  // it used to live in localStorage, was banked only when endSession() ran,
  // and so a session ended by closing the tab lost its count entirely. Riding
  // this accumulator means it flushes on visibility-hidden and beforeunload
  // too, which is exactly the failure mode that was losing the number.
  let pendingPhrasesDelta = 0

  /** Call when a cycle completes with speech detected. */
  const bumpPhraseSpoken = () => {
    pendingPhrasesDelta++
  }

  /** Call when playback starts or resumes */
  const markPlayStart = () => {
    if (!playSegmentStart) playSegmentStart = Date.now()
  }

  /** Call when playback pauses, stops, or app backgrounds */
  const markPlayStop = () => {
    if (playSegmentStart) {
      accumulatedPlaySeconds.value += Math.floor((Date.now() - playSegmentStart) / 1000)
      playSegmentStart = null
    }
    // Flush the accumulated telemetry delta (opportunities + seconds).
    // Fire-and-forget — must be fast (called from visibilitychange/unload).
    flushTelemetryDelta()
  }

  /** Get current accumulated play seconds (including any in-flight segment) */
  const getPlaySeconds = (): number => {
    let total = accumulatedPlaySeconds.value
    if (playSegmentStart) {
      total += Math.floor((Date.now() - playSegmentStart) / 1000)
    }
    return total
  }

  /**
   * Flush both deltas (opportunities counted locally per cycle + the
   * play-seconds delta since last flush) as one RPC. Safe to call
   * repeatedly — zero deltas no-op, the watermark always advances by
   * exactly what was sent, never double-counts.
   */
  const flushTelemetryDelta = () => {
    // THE SESSION ROW RIDES THE SAME BOUNDARY AS THE LEDGER (job #446).
    //
    // Until now `sessions` and `course_enrollments.total_practice_minutes`
    // were only ever written by three events — a cycle complete on the LEGACY
    // round-based path, visibility-hidden, and beforeunload — and a learner on
    // a phone who ends a sitting by tapping PAUSE and walking away fires none
    // of them that reaches the network: the tap is not a visibility change,
    // and when the OS later kills the backgrounded app there is nobody left to
    // flush the request. Measured on production 2026-09-21: one real learner,
    // twelve German sittings, every session row since 31 August still open at
    // zero seconds and zero items, while his speaking-opportunities ledger —
    // written HERE, on the pause — carried all ~255 minutes of it correctly.
    // His enrolment's minutes counter has read 3 since August for the same
    // reason.
    //
    // So the boundary that demonstrably fires is the one the session row hangs
    // off now. No new table, no new stream, no row per cycle: the same session
    // row updated in place a handful of times per sitting, and the same
    // enrolment row, by delta.
    persistSessionSnapshot()

    // Phrases ride the same boundaries but their own rpc — see
    // bumpPhrasesSpoken for why they are not a fifth argument. Drained first
    // and unconditionally, so a flush carrying only phrases (possible when
    // the seconds/opps deltas are both zero) is not swallowed by the
    // early-return below.
    const phrasesDelta = pendingPhrasesDelta
    pendingPhrasesDelta = 0
    const phrasesWrite = phrasesDelta > 0 ? bumpPhrasesSpoken(phrasesDelta) : undefined

    const totalSeconds = getPlaySeconds()
    const secondsDelta = totalSeconds - lastPersistedPlaySeconds
    const oppsDelta = pendingOppsDelta
    if (oppsDelta <= 0 && secondsDelta <= 0) return phrasesWrite
    lastPersistedPlaySeconds = totalSeconds
    pendingOppsDelta = 0
    const oppsWrite = bumpOpportunities(oppsDelta, secondsDelta)
    return phrasesWrite ? Promise.all([oppsWrite, phrasesWrite]).then(() => undefined) : oppsWrite
  }

  // TripleHelixEngine for ROUND-based learning
  const helixEngine = shallowRef<TripleHelixEngine | null>(null)
  const baskets = shallowRef<Map<string, ClassifiedBasket>>(new Map())
  const currentRoundInfo = ref<RoundInfo>({
    isInRound: false,
    legoId: null,
    phase: null,
    phaseIndex: 0,
  })

  // Helper to check if learner is a guest (guest IDs start with 'guest-')
  /**
   * Run after the boot rush. `requestIdleCallback` where it exists, with a
   * generous timeout so it cannot be starved indefinitely; a plain timer
   * everywhere else (Safari). Deliberately not tied to the player's ready
   * signal — this composable has no access to it, and "after the busy part of
   * boot" is all the guarantee this work needs.
   */
  const scheduleWhenIdle = (fn: () => void) => {
    const ric = (globalThis as { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => void }).requestIdleCallback
    if (typeof ric === 'function') ric(() => fn(), { timeout: 8000 })
    else setTimeout(fn, 4000)
  }

  const isGuestLearner = (id: string | undefined) => {
    return !id || id === 'demo-learner' || id.startsWith('guest-')
  }

  // Computed
  const isDemoMode = computed(() => !getProgressStore() || !getSessionStore())
  const hasDatabase = computed(() => Boolean(getProgressStore() && getSessionStore()))
  const hasHelixEngine = computed(() => Boolean(helixEngine.value))

  /**
   * Initialize session on mount
   */
  const initializeSession = async () => {
    isLoading.value = true
    error.value = null
    sessionStartTime.value = new Date()

    try {
      // Read stores lazily — they may not be ready at setup time
      const courseDataProvider = getCourseDataProvider()
      const sessionStore = getSessionStore()
      const progressStore = getProgressStore()
      const learnerId = getLearnerId()
      const courseId = getCourseId()

      // Try to load items from database.
      //
      // This is the LEGACY round-based path's inventory: seeds 1..30, plus one
      // `course_practice_phrases` read PER SEED for the baskets, plus the
      // TripleHelix engine build. `LearningPlayer.currentItem` reads it only
      // while `useRoundBasedPlayback` is false — that is, only before the
      // SimplePlayer has any rounds, when `demoItems` is the standing fallback
      // anyway.
      //
      // On a bundle-flagged course those 30 per-seed reads landed at ~t+2.8s on
      // a cold start, in the middle of the boot window, for data the bundle
      // already holds and a queue the learner does not play from (measured
      // 2026-08-29 — docs/first-play-wait-measured-2026-08-29.md). So there
      // they are deferred to idle rather than awaited on the way to a play
      // button. Nothing about them changes; only when they happen. An UNFLAGGED
      // course keeps the exact original ordering, awaited inline.
      const deferItemLoad = !!courseId && isBundleBootstrapEnabled(courseId)
      const loadItems = async () => {
        if (!courseDataProvider) {
          items.value = demoItems
          return
        }
        console.log('[useLearningSession] Loading items from database...')
        const dbItems = await courseDataProvider.loadSessionItems(1, 30)
        if (dbItems.length > 0) {
          items.value = dbItems
          console.log(`[useLearningSession] Loaded ${dbItems.length} items from database`)
          await initializeHelixEngine(dbItems)
        } else {
          console.log('[useLearningSession] No database items, using demo mode')
          items.value = demoItems
        }
      }
      if (deferItemLoad) {
        items.value = demoItems
        scheduleWhenIdle(() => {
          void loadItems().catch((err) => {
            console.warn('[useLearningSession] deferred item load failed (non-fatal):', err)
          })
        })
      } else {
        await loadItems()
      }

      // Start session tracking only for real learners — Supabase rejects
      // guest IDs (the `guest-` prefix breaks the uuid column constraint),
      // and the failed round-trip costs ~200ms on every cold start.
      // Diagnostic — pinpoints which guard short-circuits when sessions
      // never get written (no error is thrown so the catch below is no
      // help on its own).
      console.log('[useLearningSession] Session guard check:', {
        sessionStore: !!sessionStore,
        learnerId,
        courseId,
        isGuest: learnerId ? isGuestLearner(learnerId) : 'n/a',
      })
      // A VIEWING session leaves nothing behind (job #793): an ssi_admin
      // looking at the app as a learner must not open a session row or an
      // enrolment under their own id. The banner says read-only; this is what
      // makes that true rather than decorative.
      // The session row is history, not the first word's: it is written once
      // the first word has sounded, and nothing below waits for it (job #271).
      if (sessionStore && learnerId && courseId && !isGuestLearner(learnerId) && !isViewingAs.value) {
        void firstSound().then(() => {
          if (disposed) return null
          return sessionStore.startSession(learnerId, courseId)
        }).then((session) => {
          if (!session) return
          sessionId.value = session.id
          console.log('[useLearningSession] Session started:', session.id)
          // A pause or unmount can beat this insert. Replay that boundary
          // once its row exists, keeping the measured play time intact.
          if (endPending) return endSession()
          if (checkpointPending) checkpointSession()
        }).catch((err: any) => {
          // Surface the full error (code + hint + details) — `err.message`
          // alone hides Postgres error codes like 42501 (RLS denial) and
          // 22P02 (UUID cast failure) that pinpoint the real cause.
          console.error('[useLearningSession] Session start FAILED:', {
            message: err?.message,
            code: err?.code,
            details: err?.details,
            hint: err?.hint,
            learnerId,
            courseId,
          })
          // Continue without session tracking
        })
      }

      // Get or create enrollment if database is available (skip for guests)
      if (progressStore && learnerId && courseId && !isGuestLearner(learnerId) && !isViewingAs.value) {
        try {
          let enrollment = await progressStore.getEnrollment(learnerId, courseId)
          if (!enrollment) {
            enrollment = await progressStore.createEnrollment(learnerId, courseId)
            console.log('[useLearningSession] Created new enrollment')
          } else {
            console.log('[useLearningSession] Found existing enrollment')
          }
        } catch (err: any) {
          console.warn('[useLearningSession] Enrollment tracking unavailable:', err.message)
          // Continue without enrollment tracking
        }
        // Minutes a previous session could not bank at its end land now,
        // before the learner has played a minute of this one (job #222).
        void bankMinutes(progressStore, learnerId, courseId, 0)
      }
    } catch (err) {
      console.error('[useLearningSession] Session initialization error:', err)
      error.value = err instanceof Error ? err.message : 'Failed to initialize session'
      // Fall back to demo items
      items.value = demoItems
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Initialize TripleHelixEngine with loaded items
   */
  const initializeHelixEngine = async (loadedItems: LearningItem[]) => {
    try {
      // Create engine
      const engine = createTripleHelixEngine(
        DEFAULT_CONFIG.helix,
        DEFAULT_CONFIG.repetition,
        getCourseId()
      )

      // Convert LearningItem[] to SeedPair[] format for the engine
      const seedsMap = new Map<string, any>()
      for (const item of loadedItems) {
        const seedId = item.seed.seed_id
        if (!seedsMap.has(seedId)) {
          seedsMap.set(seedId, {
            seed_id: seedId,
            seed_pair: item.seed.seed_pair,
            legos: [],
            audioRefs: undefined, // Seeds may not have audio
          })
        }
        // Add LEGO to seed
        seedsMap.get(seedId).legos.push({
          id: item.lego.id,
          type: item.lego.type as 'A' | 'M',
          new: item.lego.new,
          lego: item.lego.lego,
          audioRefs: item.lego.audioRefs,
        })
      }

      // Load seeds into engine
      const seeds = Array.from(seedsMap.values())
      engine.loadSeeds(seeds)

      // Load baskets for LEGOs (if provider available)
      if (getCourseDataProvider()) {
        await loadBasketsForItems(loadedItems, engine)
      }

      helixEngine.value = engine
      console.log('[useLearningSession] TripleHelixEngine initialized with', seeds.length, 'seeds')
    } catch (err) {
      console.error('[useLearningSession] Failed to initialize HelixEngine:', err)
      // Continue without engine - will fall back to sequential items
    }
  }

  /**
   * Load baskets for all LEGOs in the session
   */
  const loadBasketsForItems = async (
    loadedItems: LearningItem[],
    engine: TripleHelixEngine
  ) => {
    const courseDataProvider = getCourseDataProvider()
    if (!courseDataProvider) return

    try {
      // Get unique seed IDs
      const seedIds = [...new Set(loadedItems.map(item => item.seed.seed_id))]

      // ONE request for the whole inventory. This used to be one
      // `course_practice_phrases` read per seed — thirty of them on the
      // legacy 1..30 window, all with the same filter bar `seed_number`,
      // firing in the middle of the boot window (measured 2026-08-29,
      // docs/first-play-wait-measured-2026-08-29.md). Same rows, same
      // grouping, one round-trip.
      const seedBaskets = await courseDataProvider.getLegoBasketsForSeeds(seedIds)

      for (const [legoId, basket] of seedBaskets) {
        baskets.value.set(legoId, basket)
        engine.registerBasket(legoId, basket)
      }

      console.log('[useLearningSession] Loaded baskets for', baskets.value.size, 'LEGOs')
    } catch (err) {
      console.warn('[useLearningSession] Failed to load baskets:', err)
      // Continue without baskets - engine will use simple phrases
    }
  }

  /**
   * Get the next learning item from the engine
   * Falls back to sequential items if engine not available
   */
  const getNextItem = (currentIndex: number): LearningItem | null => {
    // If we have an engine, use it for intelligent selection
    if (helixEngine.value) {
      const engineItem = helixEngine.value.getNextItem()
      if (engineItem) {
        // Update round info
        const activeRound = helixEngine.value.getActiveRound()
        if (activeRound) {
          currentRoundInfo.value = {
            isInRound: true,
            legoId: activeRound.legoId,
            phase: activeRound.roundState.current_phase,
            phaseIndex: activeRound.roundState.phase_index,
          }
        } else {
          currentRoundInfo.value = {
            isInRound: false,
            legoId: null,
            phase: null,
            phaseIndex: 0,
          }
        }

        // Convert engine LearningItem to our LearningItem format
        return {
          lego: {
            id: engineItem.lego.id,
            type: engineItem.lego.type,
            new: engineItem.lego.new,
            lego: engineItem.lego.lego,
            audioRefs: engineItem.lego.audioRefs,
          },
          phrase: {
            id: engineItem.phrase.id,
            phraseType: engineItem.phrase.phraseType,
            phrase: engineItem.phrase.phrase,
            audioRefs: engineItem.phrase.audioRefs,
            wordCount: engineItem.phrase.wordCount,
            containsLegos: engineItem.phrase.containsLegos,
          },
          seed: {
            seed_id: engineItem.seed.seed_id,
            seed_pair: engineItem.seed.seed_pair,
            legos: engineItem.seed.legos.map(l => l.id),
          },
          thread_id: engineItem.thread_id,
          mode: engineItem.mode,
        }
      }
    }

    // Fallback: sequential items
    if (currentIndex < items.value.length) {
      return items.value[currentIndex]
    }

    return null
  }

  /**
   * Lightweight cycle-complete bump for the SimplePlayer flow.
   *
   * `recordCycleComplete` below expects a `LearningItem` from the legacy
   * round-based playback path (`currentPlayableItem`), which the new
   * SimplePlayer doesn't populate. Calling it with a null item would
   * crash the helix-engine branch.
   *
   * `bumpOpportunity` is the minimal counterpart — it just advances the
   * opportunities counter so `learner_speaking_opportunities.opportunities`
   * actually increments on SimplePlayer cycle completes. Pairs with the
   * existing wall-clock `play_seconds` accumulation in `markPlayStop`.
   *
   * 2026-05-23: added to fix the "phrases = 0 forever" bug visible in the
   * belt-progress contribution modal. SimplePlayer cycle-complete events
   * were firing `contribution.incrementLocal()` (UI ticker) but the DB
   * bump path was gated on the legacy `currentPlayableItem`, which never
   * fired under SimplePlayer.
   */
  const bumpOpportunity = () => {
    itemsPracticed.value++
    pendingOppsDelta++
  }

  /**
   * Record progress for a completed cycle
   */
  const recordCycleComplete = async (item: LearningItem, wasSuccessful: boolean = true, wasSpike: boolean = false) => {
    itemsPracticed.value++

    // Accumulate locally — the RPC fires once on the next natural session
    // boundary (markPlayStop / visibility-hidden / beforeunload / endSession)
    // so we don't write per ~11s cycle. Flush logic in flushTelemetryDelta.
    pendingOppsDelta++

    // Legacy sessions.items_practiced path — kept so existing dashboards
    // that read from sessions still get checkpoint data. The
    // speaking-opportunities counter above is the new source of truth
    // for the user-facing modal.
    if (sessionId.value) {
      checkpointSession()
    }

    // Record in engine if available
    if (helixEngine.value) {
      helixEngine.value.recordPractice(
        item.lego.id,
        item.thread_id,
        wasSuccessful,
        wasSpike
      )
    }

    // Only track in database if we have stores and not a guest
    const progressStore = getProgressStore()
    const learnerId = getLearnerId()
    const courseId = getCourseId()
    if (!progressStore || !learnerId || !courseId || isGuestLearner(learnerId)) {
      return
    }

    // LEGO PROGRESS STOPS HERE while practising OR while a recycled round is
    // on the playhead (the caller passes one predicate for both) — but
    // everything above
    // (itemsPracticed, the opportunities delta, the session checkpoint, the
    // helix engine) has already run, which is the point: the learner's time
    // is recorded, their position is not.
    //
    // Not merely belt-and-braces: the recycle pool is drawn from the CACHED
    // SCRIPT, which can hold look-ahead rounds. So a practising session can
    // surface a USE phrase belonging to a LEGO the learner has never reached —
    // and writing a lego_progress row for it, or ratcheting
    // highest_completed_seed to its seed, would be exactly the inflation the
    // mode exists to prevent.
    if (isPractising()) {
      console.log('[useLearningSession] PRACTISING — usage recorded, LEGO progress not written for', item.lego.id)
      return
    }

    try {
      // Check if we already have progress for this LEGO
      const existingProgress = await progressStore.getLegoProgressById(
        learnerId,
        item.lego.id,
        courseId
      )

      if (existingProgress) {
        // Update existing progress
        await progressStore.updateLegoProgress(existingProgress.id, {
          reps_completed: existingProgress.reps_completed + 1,
          last_practiced_at: new Date(),
        })
      } else {
        // Create new progress record
        await progressStore.saveLegoProgress({
          learner_id: learnerId,
          lego_id: item.lego.id,
          course_id: courseId,
          thread_id: item.thread_id,
          // VESTIGIAL, AND KNOWINGLY SO (job #446, 2026-09-21). These two
          // columns are written exactly here, exactly once, as these two
          // constants, and nothing in this app or in @ssi/core ever advances
          // or reads them again: the live spaced repetition is DERIVED from
          // round position — `spacedRepOffsets` in
          // providers/generateLearningScript.ts, whose runtime source of truth
          // is the algorithm_config.script_shape row — and @ssi/core's
          // SpacedRepetitionQueue, the only thing that ever consumed a stored
          // rung, is not instantiated anywhere in the player. So the absence
          // of rung rows for real learners is a reporting gap, not a broken
          // engine, and DERIVATION IS THE SOURCE OF TRUTH. Left in place
          // rather than removed because the columns are NOT NULL and retiring
          // live columns is a separate, deliberate act — see
          // docs/DECISIONS.md, 2026-09-21.
          fibonacci_position: 0,
          skip_number: 1,
          reps_completed: 1,
          is_retired: false,
          last_practiced_at: new Date(),
          // ROUND tracking
          introduction_played: false,
          introduction_index: 0,
          introduction_complete: false,
          // Eternal selection
          eternal_urn: [],
          last_eternal_phrase_id: null,
        })
      }

      console.log('[useLearningSession] Progress recorded for LEGO:', item.lego.id)
    } catch (err) {
      console.error('[useLearningSession] Failed to record progress:', err)
      // Don't throw - continue learning even if tracking fails
    }

    // Update enrollment with highest seed
    try {
      const seedNumber = parseInt(item.seed.seed_id.replace(/\D/g, ''), 10)
      if (!isNaN(seedNumber) && seedNumber > 0) {
        await progressStore.updateEnrollmentActivity(
          learnerId,
          courseId,
          seedNumber,
          0  // Don't accumulate minutes per-cycle, do it on session end
        )
      }
    } catch (err) {
      console.warn('[useLearningSession] Failed to update enrollment:', err)
    }
  }

  /**
   * Lightweight checkpoint — updates session row with current progress.
   * Fire-and-forget: must be fast and never block (used in beforeunload, visibilitychange).
   */
  const checkpointSession = () => {
    const sessionStore = getSessionStore()
    if (!sessionStore || !sessionStartTime.value) return
    if (!sessionId.value) {
      checkpointPending = true
      return
    }
    checkpointPending = false

    // Use actual play seconds, not wall-clock time
    const durationSeconds = getPlaySeconds()

    // Fire-and-forget — this must be fast and never block
    const sid = sessionId.value
    const items = itemsPracticed.value
    const reportFailure = (errorClass: string, message: string, code?: string) =>
      reportCheckpointFailure({ errorClass, message, code, sessionId: sid, itemsPracticed: items, durationSeconds })
    try {
      const result = (sessionStore.checkpointSession as (...a: unknown[]) => unknown)(
        sid,
        items,
        durationSeconds,
        (f: { message: string; code?: string }) => reportFailure('SupabaseError', f.message, f.code)
      )
      Promise.resolve(result).catch((e: unknown) =>
        reportFailure((e as Error)?.name || 'Error', String((e as Error)?.message ?? e), undefined))
    } catch (e) {
      reportFailure((e as Error)?.name || 'Error', String((e as Error)?.message ?? e), undefined)
    }
  }

  // Checkpoint-failure telemetry (job #813). At most 3 reports per session and
  // one per 30 s, so a failure that repeats every tick cannot storm the log.
  let checkpointFailureCount = 0
  let lastCheckpointFailureAt = 0
  const reportCheckpointFailure = (d: Omit<CheckpointFailureDetail, 'failureCount'>) => {
    try {
      checkpointFailureCount++
      const now = Date.now()
      if (checkpointFailureCount > 3 || now - lastCheckpointFailureAt < 30_000) return
      lastCheckpointFailureAt = now
      options.onCheckpointFailed?.({
        ...d,
        message: d.message.slice(0, 200),
        errorClass: d.errorClass.slice(0, 60),
        failureCount: checkpointFailureCount,
      })
    } catch { /* telemetry never throws into checkpointing */ }
  }

  /**
   * Bank everything the play timer knows: the session row (items + play
   * seconds + a timestamp, updated in place) and the enrolment's accumulated
   * practice minutes (by DELTA, because that column adds).
   *
   * Fire-and-forget, and deliberately conservative in the same direction as
   * the play timer itself — the number may be a floor, it may never inflate
   * (owner ruling, 2026-08-19). Whole minutes only, watermarked, so calling
   * this on every pause banks each minute exactly once however many times it
   * runs. POSITION IS NOT TOUCHED: `highestSeed` is passed as 0, which
   * `updateEnrollmentActivity` folds through a Math.max, so a checkpoint can
   * never move where the learner resumes.
   */
  const persistSessionSnapshot = () => {
    checkpointSession()

    const progressStore = getProgressStore()
    const learnerId = getLearnerId()
    const courseId = getCourseId()
    if (!progressStore || !learnerId || !courseId || isGuestLearner(learnerId) || isViewingAs.value) return

    const minutes = Math.floor(getPlaySeconds() / 60)
    const delta = minutes - lastPersistedMinutes
    if (delta <= 0) return
    lastPersistedMinutes = minutes
    // A write that does not land goes to the carry (see bankMinutes), not
    // back into this watermark: the watermark dies with the composable, the
    // carry does not (job #222).
    void bankMinutes(progressStore, learnerId, courseId, delta)
  }

  const handleVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      markPlayStop()
      checkpointSession()
      return
    }
    // BACK IN THE FOREGROUND, AUDIO STILL SOUNDING — RE-ARM THE TIMER.
    //
    // Without this the play timer never restarted after a background. The
    // only thing that opens a segment is the caller's watcher on "is any
    // audio sounding", and that watcher fires on CHANGE. Lock the phone
    // mid-session and playback continues on the lock screen, so the flag
    // never goes false and never comes back true — but the hidden branch
    // above has already closed the segment. Every second from that moment
    // until the learner happened to pause and resume was banked nowhere.
    //
    // Measured against production, 2026-09-08: on the 222 learner-days since
    // the 2026-08-20 fix carrying real audio, 49 banked LESS play time than
    // the raw duration of the audio files they demonstrably played — which is
    // arithmetically impossible for an accurate counter. On real learners
    // that is 511 minutes of audio against 221 minutes banked. Those days
    // carry roughly half the pause-taps of the healthy ones and more cold
    // starts, which is the signature of exactly this path.
    //
    // Deliberately conservative: it re-arms on RETURN, so time spent listening
    // while the app is in the background is still not counted. That under-
    // counts, which is the safe direction and consistent with the founder
    // ruling of 2026-08-19 — the number may be a floor, it may never inflate.
    if (options.isAudioActive?.() === true) markPlayStart()
  }

  const handleBeforeUnload = () => {
    // markPlayStop both ends the in-flight segment and triggers a delta
    // write to the speaking-opportunities counter, so tab-close doesn't
    // lose the trailing play time.
    markPlayStop()
    checkpointSession()
  }

  /**
   * End session and save final metrics
   */
  const endSession = async () => {
    const sessionStore = getSessionStore()
    if (!sessionStore || !sessionStartTime.value) {
      return
    }
    // Stop now, even if the asynchronous insert has not returned its ID.
    markPlayStop()
    if (!sessionId.value) {
      endPending = true
      return
    }
    endPending = false

    try {
      const endTime = new Date()
      // Close the in-flight play segment BEFORE writing, so the row records
      // the trailing playback rather than dropping it. This used to run after
      // the write, which didn't matter while endSession derived duration from
      // wall-clock — now that duration IS the play total, the ordering is the
      // difference between banking the last segment and losing it.
      await sessionStore.endSession(sessionId.value, {
        session_id: sessionId.value,
        started_at: sessionStartTime.value,
        ended_at: endTime,
        items_practiced: itemsPracticed.value,
        spikes_detected: 0, // TODO: Wire up spike detection
        final_rolling_average: 0, // TODO: Wire up metrics
        metrics: [], // TODO: Wire up response metrics
        spikes: [], // TODO: Wire up spike events
      }, getPlaySeconds())

      // Update enrollment with accumulated practice minutes (actual play time)
      const progressStore = getProgressStore()
      const learnerId = getLearnerId()
      const courseId = getCourseId()
      if (progressStore && learnerId && courseId && !isGuestLearner(learnerId)) {
        // DELTA, not the whole total. Every earlier boundary in this session
        // has already banked the minutes it could see (persistSessionSnapshot),
        // and this column accumulates — sending the full figure again here
        // would count the sitting twice over.
        const durationMinutes = Math.floor(getPlaySeconds() / 60) - lastPersistedMinutes
        if (durationMinutes > 0) {
          lastPersistedMinutes += durationMinutes
          await bankMinutes(progressStore, learnerId, courseId, durationMinutes)
        }
      }

      console.log('[useLearningSession] Session ended:', sessionId.value)
    } catch (err) {
      console.error('[useLearningSession] Failed to end session:', err)
      // Don't throw on cleanup
    }
  }

  /**
   * Save session metrics (called periodically or on specific events)
   */
  const saveMetrics = async (metrics: any[]) => {
    const sessionStore = getSessionStore()
    if (!sessionStore || !sessionId.value) {
      return
    }

    try {
      await sessionStore.saveMetrics(sessionId.value, metrics)
    } catch (err) {
      console.error('[useLearningSession] Failed to save metrics:', err)
    }
  }

  /**
   * Get basket for a specific LEGO
   */
  const getBasket = (legoId: string): ClassifiedBasket | undefined => {
    return baskets.value.get(legoId)
  }

  // Lifecycle hooks
  onMounted(() => {
    initializeSession()

    // Checkpoint when tab becomes hidden (user switches tabs, locks phone, etc.)
    document.addEventListener('visibilitychange', handleVisibilityChange)
    // Last-resort checkpoint on tab close
    window.addEventListener('beforeunload', handleBeforeUnload)
  })

  onUnmounted(() => {
    disposed = true
    document.removeEventListener('visibilitychange', handleVisibilityChange)
    window.removeEventListener('beforeunload', handleBeforeUnload)
    endSession()
  })

  return {
    // State
    sessionId,
    isLoading,
    error,
    items,
    isDemoMode,
    hasDatabase,
    hasHelixEngine,
    itemsPracticed,
    currentRoundInfo,

    // Methods
    getNextItem,
    recordCycleComplete,
    bumpOpportunity,
    // Cycles where the VAD heard the learner. Flushed by flushTelemetryDelta
    // on the same boundaries as everything else, so tab-close keeps it.
    bumpPhraseSpoken,
    // Persist accumulated play-seconds WITHOUT ending the play segment — used to
    // bring the stats modal current on open (markPlayStop would stop the timer).
    flushTelemetryDelta,
    checkpointSession,
    saveMetrics,
    endSession,
    getBasket,
    markPlayStart,
    markPlayStop,
  }
}
